// FTE Calculation Utilities — SAGE

export const DEFAULT_EFFECTIVE_HOURS = {
  gross_working_days: 260,
  public_holidays: 10,
  annual_leave_expat: 21,
  annual_leave_local: 30,
  sick_leave_days: 5,
  other_absence_days: 3,
  shift_hours_expat: 8,
  shift_hours_local: 5,
  lunch_break: 1,
  prayer_break: 0.5,
};

export function calcEffectiveHours(eh = {}) {
  const d = { ...DEFAULT_EFFECTIVE_HOURS, ...eh };
  const ndE = Math.max(0, d.gross_working_days - d.public_holidays - d.annual_leave_expat - d.sick_leave_days - d.other_absence_days);
  const ndL = Math.max(0, d.gross_working_days - d.public_holidays - d.annual_leave_local - d.sick_leave_days - d.other_absence_days);
  const phE = Math.max(0, d.shift_hours_expat - d.prayer_break);
  const phL = Math.max(0, d.shift_hours_local - d.prayer_break);
  const annualHrsExpat = ndE * phE;
  const annualHrsLocal = ndL * phL;
  const monthlyHrsExpat = annualHrsExpat / 12;
  const monthlyHrsLocal = annualHrsLocal / 12;

  return {
    netDaysExpat: ndE,
    netDaysLocal: ndL,
    prodHrsDayExpat: phE,
    prodHrsDayLocal: phL,
    annualHrsExpat,
    annualHrsLocal,
    monthlyHrsExpat,
    monthlyHrsLocal,
    conversionFactor: monthlyHrsLocal > 0 ? (monthlyHrsExpat / monthlyHrsLocal).toFixed(2) : "—",
    capacityGapPct: monthlyHrsExpat > 0 ? ((monthlyHrsExpat - monthlyHrsLocal) / monthlyHrsExpat * 100).toFixed(1) : 0,
  };
}

export function calcRequiredFTE(stage, totalMonthlyVolume, effHours, calOverride = null) {
  const lc = stage.local_count || 0;
  const ec = stage.expat_count || 0;
  const tc = lc + ec;
  if (tc === 0 || !totalMonthlyVolume) return 0;

  const sv = totalMonthlyVolume * ((stage.yield_pct || 100) / 100);
  const blendedHrs = (lc * effHours.monthlyHrsLocal + ec * effHours.monthlyHrsExpat) / tc;
  const util = (calOverride?.utilization ?? stage.utilization ?? 75) / 100;
  const prod = (calOverride?.productivity ?? stage.productivity ?? 80) / 100;

  const totalMinsNeeded = sv * (stage.aht || 10) / Math.max(prod, 0.01);
  const availMinsPerFTE = blendedHrs * 60 * Math.max(util, 0.01);
  if (availMinsPerFTE === 0) return 0;

  return totalMinsNeeded / availMinsPerFTE;
}

export function calcLocalization(stage, orgProfile = {}, effHours) {
  const lc = stage.local_count || 0;
  const ec = stage.expat_count || 0;
  const tc = lc + ec;
  if (tc === 0) return { fteCountBasis: 0, effortWeighted: 0, flag: "ok", diff: 0 };

  const fteCountBasis = (lc / tc) * 100;
  const totalEffortLocal = lc * (effHours?.monthlyHrsLocal || 0);
  const totalEffortExpat = ec * (effHours?.monthlyHrsExpat || 0);
  const totalEffort = totalEffortLocal + totalEffortExpat;
  const effortWeighted = totalEffort > 0 ? (totalEffortLocal / totalEffort) * 100 : 0;

  const target = orgProfile?.localization_target ?? 20;
  const tolerance = orgProfile?.tolerance_band ?? 5;
  const diff = fteCountBasis - target;

  let flag = "ok";
  if (Math.abs(diff) > tolerance) {
    flag = diff > 0 ? "over" : "under";
  }

  return { fteCountBasis: fteCountBasis.toFixed(1), effortWeighted: effortWeighted.toFixed(1), flag, diff: diff.toFixed(1) };
}

export function calcWasteFootprint(qualityWaste = {}, totalFTE = 0) {
  const nva = qualityWaste.nva_time || 0;
  const rework = qualityWaste.rework_rate || 0;
  const absenteeism = qualityWaste.absenteeism_rate || 0;

  const nvaFteHours = totalFTE * (nva / 100);
  const reworkFteHours = totalFTE * (rework / 100);
  const absentFte = totalFTE * (absenteeism / 100);
  const totalWastePct = Math.min(100, nva + rework + absenteeism);

  return { nva, rework, absenteeism, nvaFteHours: nvaFteHours.toFixed(1), reworkFteHours: reworkFteHours.toFixed(1), absentFte: absentFte.toFixed(1), totalWastePct: totalWastePct.toFixed(1) };
}

export function calcFutureStateWave(stages, totalVolume, effHours, wave) {
  const waves = {
    1: { prodGain: 8, utilGain: 5, nvаRed: 30, reworkRed: 20 },
    2: { prodGain: 18, utilGain: 10, nvaRed: 55, reworkRed: 45 },
    3: { prodGain: 30, utilGain: 15, nvaRed: 75, reworkRed: 70 },
  };
  const w = waves[wave] || waves[1];

  let totalReqFTE = 0;
  for (const s of stages) {
    const improved = {
      ...s,
      productivity: Math.min(130, (s.productivity || 80) * (1 + w.prodGain / 100)),
      utilization: Math.min(100, (s.utilization || 75) * (1 + w.utilGain / 100)),
    };
    totalReqFTE += calcRequiredFTE(improved, totalVolume, effHours);
  }
  return totalReqFTE;
}

export function calcFTERelease(stage, totalVolume, effHours, improvement) {
  const { type, magnitude } = improvement;
  const currentReqFTE = calcRequiredFTE(stage, totalVolume, effHours);

  let improved = { ...stage };
  if (type === "productivity" || type === "both") {
    improved.productivity = Math.min(130, (stage.productivity || 80) * (1 + magnitude / 100));
  }
  if (type === "utilization" || type === "both") {
    improved.utilization = Math.min(100, (stage.utilization || 75) * (1 + magnitude / 100));
  }

  const newReqFTE = calcRequiredFTE(improved, totalVolume, effHours);
  const releaseTotal = Math.max(0, currentReqFTE - newReqFTE);

  const tc = (stage.local_count || 0) + (stage.expat_count || 0);
  const currentLocalPct = tc > 0 ? (stage.local_count || 0) / tc * 100 : 0;
  const locTarget = 20;
  const minLocalPct = Math.max(currentLocalPct, locTarget) / 100;

  const releaseExpat = releaseTotal * (1 - minLocalPct);
  const releaseLocal = releaseTotal - releaseExpat;

  const remainingLocal = Math.max(0, (stage.local_count || 0) - releaseLocal);
  const remainingExpat = Math.max(0, (stage.expat_count || 0) - releaseExpat);
  const remainingTotal = remainingLocal + remainingExpat;
  const postLocalPct = remainingTotal > 0 ? (remainingLocal / remainingTotal) * 100 : 0;

  return {
    currentReqFTE: currentReqFTE.toFixed(1),
    newReqFTE: newReqFTE.toFixed(1),
    releaseTotal: releaseTotal.toFixed(1),
    releaseLocal: releaseLocal.toFixed(1),
    releaseExpat: releaseExpat.toFixed(1),
    remainingLocal: remainingLocal.toFixed(0),
    remainingExpat: remainingExpat.toFixed(0),
    postLocalPct: postLocalPct.toFixed(1),
    localizationOk: postLocalPct >= locTarget,
  };
}
